// Ported verbatim from files/snapdraw-design-kit/code/src/lib/i18n/strings/contracts.ts
// (Lovable reference implementation), Rule 17 — the Contract authoring
// pages (/contracts, /contracts/new, /contracts/:id/edit). contractPicker.*
// (used by @/components/snapdraw/contract-picker) lived in the kit's
// documents.ts string file; kept together here since it's the same domain.
export const en = {
  "contractPicker.title": "Choose a contract",
  "contractPicker.body":
    "Every pay application belongs to a contract. Picking it carries the approved schedule of values and last month's billings forward.",
  "contractPicker.search": "Search contracts, projects, GCs…",
  "contractPicker.empty": "No contracts match that search.",
  "contractPicker.through": "Billed through App #{n}",
  "contractPicker.firstApp": "No applications yet",
  "contractPicker.next": "Next: App #{n}",

  "contracts.eyebrow": "Awarded work",
  "contracts.title": "Contracts",
  "contracts.subtitle":
    "The parent record every G702/G703 hangs off: the approved schedule of values, retainage and the change order ledger.",
  "contracts.searchPlaceholder": "Search contracts, projects, GCs…",
  "contracts.new": "New Contract",
  "contracts.empty.title": "No contracts yet",
  "contracts.empty.body":
    "Set a contract up once at award. Every pay application on that job then carries the schedule of values and prior billings forward automatically.",
  "contracts.card.sumToDate": "Contract sum to date",
  "contracts.card.lines": "{count} SOV line(s)",
  "contracts.card.cos": "{count} change order(s)",
  "contracts.card.apps": "Billed through App #{n}",
  "contracts.card.noApps": "No applications yet",
  "contracts.card.applications": "{count} application(s)",
  "contracts.apps.app": "App #{n}",
  "contracts.apps.periodTo": "Period to {date}",
  "contracts.apps.sent": "Sent {date}",
  "contracts.apps.openDoc": "Open application",
  "contracts.notFound.title": "Contract not found",
  "contracts.notFound.body": "That contract no longer exists or the link is out of date.",

  "contracts.new.eyebrow": "Awarded work",
  "contracts.new.title": "New Contract",
  "contracts.new.subtitle":
    "Set this up once at award. Column C is frozen after this — only executed change orders move it.",
  "contracts.new.save": "Save Contract",
  "contracts.edit.eyebrow": "Contract",
  "contracts.edit.title": "Edit Contract",
  "contracts.edit.subtitle":
    "Editing an awarded contract re-bases every future application. Prior applications keep the values they were billed at.",
  "contracts.edit.save": "Save Changes",
  "contracts.back": "All contracts",
  "contracts.cancel": "Cancel",
  "contracts.saveAndApp": "Save & Start Application",
  "contracts.saved": "{name} saved.",

  "contracts.panel.identity": "Contract",
  "contracts.panel.money": "Contract Sum & Retainage",
  "contracts.panel.sov": "Schedule of Values",
  "contracts.panel.sovSub": "Column A · B · C",
  "contracts.panel.co": "Change Orders",
  "contracts.panel.summary": "Summary",

  "contracts.field.name": "Project Name",
  "contracts.field.client": "General Contractor / Owner",
  "contracts.field.number": "Contract Number",
  "contracts.field.date": "Contract Date",
  "contracts.field.sum": "Original Contract Sum",
  "contracts.field.retainage": "Retainage %",
  "contracts.placeholder.name": "Riverside Tower",
  "contracts.placeholder.number": "CT-2026-0007",
  "contracts.required": "Required",
  "contracts.error.name": "Project name is required.",
  "contracts.error.client": "Pick the GC or owner this contract is with.",
  "contracts.error.sum": "Enter the original contract sum from the executed agreement.",
  "contracts.chooseClient": "Choose client",
  "contracts.changeClient": "Change",
  "contracts.retainageHint": "Withheld on each application until release.",
  "contracts.sumHint": "Line 1 on every G702. Fixed for the life of the job.",

  "contracts.sov.hint": "These lines are frozen once the contract is awarded. Each application bills against them.",
  "contracts.sov.item": "Item #",
  "contracts.sov.description": "Description of work",
  "contracts.sov.scheduled": "Scheduled value",
  "contracts.sov.namePlaceholder": "Rough-in — levels 1–8",
  "contracts.sov.add": "Add SOV line",
  "contracts.sov.remove": "Remove line",
  "contracts.sov.up": "Move line up",
  "contracts.sov.down": "Move line down",
  "contracts.sov.renumber": "Renumber",
  "contracts.sov.n": "Line {n}",
  "contracts.sov.total": "Schedule of values total",
  "contracts.sov.empty.title": "No schedule of values yet",
  "contracts.sov.empty.body": "Add the approved lines from the executed contract. Item numbers are suggested and stay editable.",
  "contracts.sov.fromCo": "From {number}",
  "contracts.sov.balanced": "Schedule of values foots to the contract sum to date.",
  "contracts.sov.off": "Off by {amount} against the contract sum to date ({sum}).",

  "contracts.co.hint": "A change order moves line 2 and the scheduled value it touches, so the two can never drift apart.",
  "contracts.co.number": "CO #",
  "contracts.co.date": "Date executed",
  "contracts.co.description": "Description",
  "contracts.co.amount": "Amount",
  "contracts.co.target": "Applies to",
  "contracts.co.newLine": "New SOV line",
  "contracts.co.add": "Add change order",
  "contracts.co.remove": "Remove change order",
  "contracts.co.empty": "No change orders executed yet.",
  "contracts.co.net": "Net change by change orders",
  "contracts.co.descriptionPlaceholder": "Added EV rough-in, P2 parking",
  "contracts.co.error": "A change order needs a number and a non-zero amount.",

  "contracts.summary.line1": "1 · Original contract sum",
  "contracts.summary.line2": "2 · Net change by change orders",
  "contracts.summary.line3": "3 · Contract sum to date",
  "contracts.summary.retainage": "Retainage withheld",
  "contracts.summary.sovTotal": "Schedule of values total",
  "contracts.summary.lines": "{count} line(s)",
  "contracts.summary.hint": "Every application on this job opens with these values and carries prior billings forward.",

  "contracts.edit.button": "Edit Contract",
  "contracts.detail.continue": "Continue → Application #{n}",
  "contracts.detail.resume": "Resume Application #{n}",
  "contracts.detail.startNext": "Start application #{n}",
  "contracts.detail.inProgress": "Started {date} · {status}",
  "contracts.detail.executed": "Executed {date}",
  "contracts.detail.billedToDate": "Billed to date",
  "contracts.detail.remaining": "Remaining",
  "contracts.detail.applications": "Applications",
  "contracts.apps.empty.title": "No applications yet",
  "contracts.apps.empty.body": "Bill the first period against this contract and it will appear here.",
} as const;

export const es: Record<keyof typeof en, string> = {
  "contractPicker.title": "Elegir un contrato",
  "contractPicker.body":
    "Toda solicitud de pago pertenece a un contrato. Al elegirlo se arrastran el programa de valores aprobado y la facturación del mes anterior.",
  "contractPicker.search": "Buscar contratos, proyectos, contratistas…",
  "contractPicker.empty": "Ningún contrato coincide con la búsqueda.",
  "contractPicker.through": "Facturado hasta la solicitud #{n}",
  "contractPicker.firstApp": "Aún sin solicitudes",
  "contractPicker.next": "Siguiente: solicitud #{n}",

  "contracts.eyebrow": "Trabajo adjudicado",
  "contracts.title": "Contratos",
  "contracts.subtitle":
    "El registro padre del que cuelga cada G702/G703: el programa de valores aprobado, la retención y el registro de órdenes de cambio.",
  "contracts.searchPlaceholder": "Buscar contratos, proyectos, contratistas…",
  "contracts.new": "Nuevo contrato",
  "contracts.empty.title": "Aún no hay contratos",
  "contracts.empty.body":
    "Configúrelo una vez al adjudicar. Cada solicitud de pago arrastrará el programa de valores y la facturación anterior automáticamente.",
  "contracts.card.sumToDate": "Suma del contrato a la fecha",
  "contracts.card.lines": "{count} línea(s) del programa",
  "contracts.card.cos": "{count} orden(es) de cambio",
  "contracts.card.apps": "Facturado hasta la solicitud #{n}",
  "contracts.card.noApps": "Aún sin solicitudes",
  "contracts.card.applications": "{count} solicitud(es)",
  "contracts.apps.app": "Solicitud #{n}",
  "contracts.apps.periodTo": "Período hasta {date}",
  "contracts.apps.sent": "Enviada {date}",
  "contracts.apps.openDoc": "Abrir solicitud",
  "contracts.notFound.title": "Contrato no encontrado",
  "contracts.notFound.body": "Ese contrato ya no existe o el enlace está desactualizado.",

  "contracts.new.eyebrow": "Trabajo adjudicado",
  "contracts.new.title": "Nuevo contrato",
  "contracts.new.subtitle":
    "Configúrelo una vez al adjudicar. La columna C queda fija: solo las órdenes de cambio ejecutadas la mueven.",
  "contracts.new.save": "Guardar contrato",
  "contracts.edit.eyebrow": "Contrato",
  "contracts.edit.title": "Editar contrato",
  "contracts.edit.subtitle":
    "Editar un contrato adjudicado recalcula las solicitudes futuras. Las anteriores conservan lo facturado.",
  "contracts.edit.save": "Guardar cambios",
  "contracts.back": "Todos los contratos",
  "contracts.cancel": "Cancelar",
  "contracts.saveAndApp": "Guardar e iniciar solicitud",
  "contracts.saved": "{name} guardado.",

  "contracts.panel.identity": "Contrato",
  "contracts.panel.money": "Suma del contrato y retención",
  "contracts.panel.sov": "Programa de valores",
  "contracts.panel.sovSub": "Columna A · B · C",
  "contracts.panel.co": "Órdenes de cambio",
  "contracts.panel.summary": "Resumen",

  "contracts.field.name": "Nombre del proyecto",
  "contracts.field.client": "Contratista general / propietario",
  "contracts.field.number": "Número de contrato",
  "contracts.field.date": "Fecha del contrato",
  "contracts.field.sum": "Suma original del contrato",
  "contracts.field.retainage": "Retención %",
  "contracts.placeholder.name": "Riverside Tower",
  "contracts.placeholder.number": "CT-2026-0007",
  "contracts.required": "Obligatorio",
  "contracts.error.name": "El nombre del proyecto es obligatorio.",
  "contracts.error.client": "Elija el contratista o propietario del contrato.",
  "contracts.error.sum": "Ingrese la suma original del contrato ejecutado.",
  "contracts.chooseClient": "Elegir cliente",
  "contracts.changeClient": "Cambiar",
  "contracts.retainageHint": "Se retiene en cada solicitud hasta su liberación.",
  "contracts.sumHint": "Línea 1 de cada G702. Fija durante toda la obra.",

  "contracts.sov.hint": "Estas líneas quedan fijas al adjudicar el contrato. Cada solicitud factura contra ellas.",
  "contracts.sov.item": "Ítem #",
  "contracts.sov.description": "Descripción del trabajo",
  "contracts.sov.scheduled": "Valor programado",
  "contracts.sov.namePlaceholder": "Instalación — niveles 1–8",
  "contracts.sov.add": "Agregar línea",
  "contracts.sov.remove": "Eliminar línea",
  "contracts.sov.up": "Subir línea",
  "contracts.sov.down": "Bajar línea",
  "contracts.sov.renumber": "Renumerar",
  "contracts.sov.n": "Línea {n}",
  "contracts.sov.total": "Total del programa de valores",
  "contracts.sov.empty.title": "Aún no hay programa de valores",
  "contracts.sov.empty.body": "Agregue las líneas aprobadas del contrato ejecutado. Los números de ítem se sugieren y se pueden editar.",
  "contracts.sov.fromCo": "De {number}",
  "contracts.sov.balanced": "El programa de valores cuadra con la suma del contrato a la fecha.",
  "contracts.sov.off": "Diferencia de {amount} contra la suma del contrato a la fecha ({sum}).",

  "contracts.co.hint": "Una orden de cambio mueve la línea 2 y el valor programado que toca, para que nunca se separen.",
  "contracts.co.number": "OC #",
  "contracts.co.date": "Fecha de ejecución",
  "contracts.co.description": "Descripción",
  "contracts.co.amount": "Monto",
  "contracts.co.target": "Se aplica a",
  "contracts.co.newLine": "Nueva línea del programa",
  "contracts.co.add": "Agregar orden de cambio",
  "contracts.co.remove": "Eliminar orden de cambio",
  "contracts.co.empty": "Aún no hay órdenes de cambio ejecutadas.",
  "contracts.co.net": "Cambio neto por órdenes de cambio",
  "contracts.co.descriptionPlaceholder": "Instalación para cargadores EV, estacionamiento P2",
  "contracts.co.error": "Una orden de cambio necesita número y un monto distinto de cero.",

  "contracts.summary.line1": "1 · Suma original del contrato",
  "contracts.summary.line2": "2 · Cambio neto por órdenes de cambio",
  "contracts.summary.line3": "3 · Suma del contrato a la fecha",
  "contracts.summary.retainage": "Retención",
  "contracts.summary.sovTotal": "Total del programa de valores",
  "contracts.summary.lines": "{count} línea(s)",
  "contracts.summary.hint": "Cada solicitud de esta obra abre con estos valores y arrastra la facturación anterior.",

  "contracts.edit.button": "Editar contrato",
  "contracts.detail.continue": "Continuar → Solicitud #{n}",
  "contracts.detail.resume": "Reanudar solicitud #{n}",
  "contracts.detail.startNext": "Iniciar solicitud #{n}",
  "contracts.detail.inProgress": "Iniciada el {date} · {status}",
  "contracts.detail.executed": "Firmado el {date}",
  "contracts.detail.billedToDate": "Facturado a la fecha",
  "contracts.detail.remaining": "Restante",
  "contracts.detail.applications": "Solicitudes",
  "contracts.apps.empty.title": "Aún no hay solicitudes",
  "contracts.apps.empty.body": "Facture el primer período contra este contrato y aparecerá aquí.",
};
